import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query"
import {
	Archive,
	ArchiveRestore,
	Megaphone,
	Pencil,
	Trash2,
	UserMinus,
	UserPlus,
} from "lucide-react"
import { useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import {
	type AudienceFilter,
	audienceKeys,
	bulkTagMembers,
	listAudience,
	plural,
} from "@/entities/audience"
import { getTagStats, statsKeys } from "@/entities/bot-stats"
import { useSessionRole } from "@/entities/session"
import { AddMembersDialog, BulkGroupMenu } from "@/features/manage-groups"
import { AUDIENCE_PRESETS, useStartBroadcast } from "@/features/send-broadcast"
import { useDebounced } from "@/shared/hooks/use-debounced"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { EmptyState } from "@/shared/ui/empty-state"
import { Input } from "@/shared/ui/input"
import { RowActionsMenu } from "@/shared/ui/row-actions-menu"
import { AudienceListSection } from "@/widgets/audience-list"
import {
	botPath,
	type LoadedDraft,
	useBotWorkspace,
	WithDraft,
	WorkspaceSection,
} from "@/widgets/bot-workspace"

const TITLE = "Группа"
const PAGE_SIZE = 50
const PEOPLE: [string, string, string] = ["человек", "человека", "человек"]
const AUTO_PREFIX = "auto-"

/** Одна группа: состав, добавить и убрать людей, переименовать, убрать в архив. */
export function GroupPage() {
	return <WithDraft title={TITLE}>{(state) => <GroupContent state={state} />}</WithDraft>
}

function GroupContent({ state }: { state: LoadedDraft }) {
	const { groupId = "" } = useParams()
	const presetId = groupId.startsWith(AUTO_PREFIX) ? groupId.slice(AUTO_PREFIX.length) : null
	const preset = presetId ? AUDIENCE_PRESETS.find((item) => item.id === presetId) : undefined
	const tag = presetId ? undefined : state.draft.tags?.find((item) => item.id === groupId)

	if (!preset && !tag) {
		return (
			<WorkspaceSection title={TITLE} eyebrow="Группа">
				<EmptyState icon={Pencil} title="Такой группы нет">
					Возможно, её удалили. Вернитесь к списку групп.
				</EmptyState>
			</WorkspaceSection>
		)
	}
	return preset ? (
		<AutoGroup label={preset.label} hint={preset.hint} filterOf={preset.filter} />
	) : tag ? (
		<ManualGroup state={state} tag={tag} />
	) : null
}

function useMembers(botId: string, filter: AudienceFilter, sort: string) {
	const { isModerator } = useSessionRole()
	return useInfiniteQuery({
		queryKey: [...audienceKeys.list(botId, filter), sort],
		queryFn: ({ pageParam }) =>
			listAudience(botId, filter, { skip: pageParam, take: PAGE_SIZE, sort }),
		initialPageParam: 0,
		getNextPageParam: (last, pages) => {
			const loaded = pages.reduce((sum, page) => sum + page.items.length, 0)
			return loaded < last.total ? loaded : undefined
		},
		placeholderData: keepPreviousData,
		enabled: isModerator && !!botId,
	})
}

/** Готовая группа считается по условиям на лету: состав смотреть можно, править нельзя. */
function AutoGroup({
	label,
	hint,
	filterOf,
}: {
	label: string
	hint: string
	filterOf: () => AudienceFilter
}) {
	const botId = useBotWorkspace().bot.id
	const navigate = useNavigate()
	const startBroadcast = useStartBroadcast(botId)
	const base = useMemo(filterOf, [filterOf])
	const [search, setSearch] = useState("")
	const [sort, setSort] = useState("active")
	const debounced = useDebounced(search.trim(), 300)
	const filter: AudienceFilter = { ...base, ...(debounced ? { search: debounced } : {}) }
	const members = useMembers(botId, filter, sort)
	const pages = members.data?.pages ?? []
	const total = pages[0]?.total ?? 0

	return (
		<WorkspaceSection
			title={label}
			eyebrow="Автоматическая группа"
			subtitle={`${hint}. Состав считается сам, вручную его не поменять.`}
			actions={
				<Button
					className="rounded-xl"
					disabled={startBroadcast.isPending || total === 0}
					onClick={() => startBroadcast.mutate({ name: label, filter: base })}
				>
					<Megaphone />
					Рассылка
				</Button>
			}
		>
			<AudienceListSection
				search={search}
				onSearchChange={setSearch}
				variables={pages[0]?.variables ?? []}
				exporting={false}
				total={total}
				filtered={false}
				isPlaceholderData={members.isPlaceholderData}
				listKey={`${JSON.stringify(filter)}-${sort}`}
				isLoading={members.isLoading}
				isError={members.isError}
				onRetry={() => void members.refetch()}
				members={pages.flatMap((page) => page.items)}
				botUsername={undefined}
				onResetFilters={() => setSearch("")}
				onOpenMember={(memberId) => navigate(botPath(botId, `analytics/users/${memberId}`))}
				hasNextPage={members.hasNextPage}
				isFetchingNextPage={members.isFetchingNextPage}
				onFetchNextPage={() => void members.fetchNextPage()}
				sort={sort}
				onSortChange={setSort}
			/>
		</WorkspaceSection>
	)
}

function ManualGroup({
	state,
	tag,
}: {
	state: LoadedDraft
	tag: { id: string; name: string; archivedAt: string | null }
}) {
	const botId = useBotWorkspace().bot.id
	const navigate = useNavigate()
	const { confirm } = useConfirm()
	const startBroadcast = useStartBroadcast(botId)
	const [search, setSearch] = useState("")
	const [sort, setSort] = useState("active")
	const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
	const [adding, setAdding] = useState(false)
	const [renaming, setRenaming] = useState<string | null>(null)
	const debounced = useDebounced(search.trim(), 300)
	const filter: AudienceFilter = { tagId: tag.id, ...(debounced ? { search: debounced } : {}) }
	const members = useMembers(botId, filter, sort)
	const stats = useQuery({ queryKey: statsKeys.tags(botId), queryFn: () => getTagStats(botId) })
	const pages = members.data?.pages ?? []
	const items = pages.flatMap((page) => page.items)
	const total = pages[0]?.total ?? 0
	const statsCount = stats.data?.tags.find((row) => row.tagId === tag.id)?.count
	const count = debounced ? (statsCount ?? total) : pages.length > 0 ? total : (statsCount ?? 0)
	const archived = !!tag.archivedAt
	const otherTags = (state.draft.tags ?? []).filter(
		(item) => item.id !== tag.id && !item.archivedAt,
	)
	const name = renaming?.trim() ?? ""
	const duplicate = (state.draft.tags ?? []).some(
		(item) => item.id !== tag.id && item.name.toLowerCase() === name.toLowerCase(),
	)

	const toggle = (id: string, on: boolean) => {
		const next = new Set(picked)
		if (on) next.add(id)
		else next.delete(id)
		setPicked(next)
	}
	const removePicked = () =>
		confirm({
			title: `Убрать ${picked.size} ${plural(picked.size, PEOPLE)} из «${tag.name}»?`,
			message: "Сами люди останутся в боте, у них снимется только эта группа.",
			confirmText: "Убрать",
			onConfirm: async () => {
				try {
					const { changed } = await bulkTagMembers(botId, tag.id, "remove", {
						sessionIds: [...picked],
					})
					setPicked(new Set())
					await members.refetch()
					await stats.refetch()
					toast.success(`Убрано: ${changed} ${plural(changed, PEOPLE)}`)
				} catch {
					toast.error("Не удалось убрать людей из группы")
				}
			},
		})

	const menu = (
		<RowActionsMenu
			label={`Действия с группой ${tag.name}`}
			trigger={
				<Button
					variant="outline"
					size="icon"
					className="rounded-xl"
					aria-label="Действия с группой"
				>
					<span aria-hidden>⋯</span>
				</Button>
			}
			actions={[
				{ label: "Переименовать", icon: Pencil, onSelect: () => setRenaming(tag.name) },
				{
					label: archived ? "Вернуть из архива" : "Убрать в архив",
					icon: archived ? ArchiveRestore : Archive,
					onSelect: () => void state.actions.setTagArchived(tag.id, !archived),
				},
				{
					label: "Удалить группу",
					icon: Trash2,
					destructive: true,
					separatorBefore: true,
					onSelect: () =>
						confirm({
							title: `Удалить группу «${tag.name}»?`,
							message:
								"Сами люди останутся, пропадёт только группа. Блоки воронок, которые в неё добавляли, нужно будет поправить. Если группу просто не нужно показывать, лучше убрать её в архив.",
							confirmText: "Удалить",
							onConfirm: async () => {
								await state.actions.deleteTag(tag.id)
								navigate(botPath(botId, "analytics/groups"))
							},
						}),
				},
			]}
		/>
	)

	const selectionBar =
		picked.size > 0 ? (
			<>
				<span className="text-sm">
					Отмечено: <span className="tabular-nums">{picked.size}</span>
				</span>
				<Button variant="outline" className="h-10 rounded-xl" onClick={removePicked}>
					<UserMinus />
					<span className="hidden sm:inline">Убрать из группы</span>
				</Button>
				<BulkGroupMenu
					botId={botId}
					filter={{ sessionIds: [...picked] }}
					total={picked.size}
					tags={otherTags}
					picked
					onDone={() => setPicked(new Set())}
				/>
				<Button variant="ghost" className="h-10 rounded-xl" onClick={() => setPicked(new Set())}>
					Снять отметку
				</Button>
			</>
		) : null

	return (
		<WorkspaceSection
			title={tag.name}
			eyebrow={archived ? "Группа · в архиве" : "Группа"}
			subtitle={`${count} ${plural(count, PEOPLE)}`}
			actions={
				<>
					<Button className="rounded-xl" onClick={() => setAdding(true)}>
						<UserPlus />
						Добавить людей
					</Button>
					<Button
						variant="outline"
						className="rounded-xl"
						disabled={startBroadcast.isPending || count === 0}
						onClick={() =>
							startBroadcast.mutate({ name: `Группе «${tag.name}»`, filter: { tagId: tag.id } })
						}
					>
						<Megaphone />
						Рассылка
					</Button>
					{menu}
				</>
			}
		>
			<AudienceListSection
				search={search}
				onSearchChange={setSearch}
				variables={pages[0]?.variables ?? []}
				exporting={false}
				total={total}
				filtered={!!debounced}
				isPlaceholderData={members.isPlaceholderData}
				listKey={`${JSON.stringify(filter)}-${sort}`}
				isLoading={members.isLoading}
				isError={members.isError}
				onRetry={() => void members.refetch()}
				members={items}
				botUsername={undefined}
				onResetFilters={() => setSearch("")}
				onOpenMember={(memberId) => navigate(botPath(botId, `analytics/users/${memberId}`))}
				hasNextPage={members.hasNextPage}
				isFetchingNextPage={members.isFetchingNextPage}
				onFetchNextPage={() => void members.fetchNextPage()}
				sort={sort}
				onSortChange={setSort}
				selectedIds={picked}
				onToggleMember={(memberId) => toggle(memberId, !picked.has(memberId))}
				onToggleAll={() => {
					const all = items.every((member) => picked.has(member.id))
					const next = new Set(picked)
					for (const member of items) {
						if (all) next.delete(member.id)
						else next.add(member.id)
					}
					setPicked(next)
				}}
				selectionBar={selectionBar}
			/>

			<AddMembersDialog botId={botId} tag={tag} open={adding} onOpenChange={setAdding} />

			<Dialog open={renaming !== null} onOpenChange={(open) => !open && setRenaming(null)}>
				<DialogContent className="sm:max-w-sm">
					<DialogHeader>
						<DialogTitle>Переименовать группу</DialogTitle>
						<DialogDescription>
							Люди останутся в группе, поменяется только название.
						</DialogDescription>
					</DialogHeader>
					<form
						className="space-y-2"
						onSubmit={(event) => {
							event.preventDefault()
							if (!name || duplicate) return
							void state.actions.renameTag(tag.id, name)
							setRenaming(null)
						}}
					>
						<Input
							autoFocus
							aria-label="Название группы"
							maxLength={64}
							value={renaming ?? ""}
							onChange={(event) => setRenaming(event.target.value)}
						/>
						{duplicate ? <p className="text-xs text-amber-300">Такая группа уже есть</p> : null}
						<DialogFooter>
							<Button type="submit" disabled={!name || duplicate}>
								Сохранить
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</WorkspaceSection>
	)
}
